/**
 * useProbeBriefing — ask the server to read the examiner's file as soon as
 * the Probe page opens.
 *
 * The file (policies, findings, regulator changes, the authority, the
 * person's past rehearsals) is a dozen domain-API reads. Requesting it on
 * open means the server has it warm by the time Start is pressed, so the
 * call costs one SDP exchange; the answer also tells the page who the
 * examiner thinks it is speaking with.
 *
 * @module components/regulation/probe/useProbeBriefing
 */
import { ProbeBriefing, type ProbeBriefing as ProbeBriefingType } from "@amoreg/contracts";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { useCallback, useEffect, useState } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../../../environments/primary/target";
import { isRestrictedProvidersRefusal } from "./probeRefusal";

const decodeBriefing = Schema.decodeUnknownOption(ProbeBriefing);

/** Why there is no briefing: the organization's AI providers rule Probe out, or the read failed. */
export type ProbeBriefingFailure = "restricted" | "failed";

export const fetchProbeBriefing = async (
  organizationId: string,
  signal?: AbortSignal,
): Promise<ProbeBriefingType | ProbeBriefingFailure> => {
  const response = await fetch(
    resolvePrimaryEnvironmentHttpUrl("/api/amoreg/probe/briefing", { organizationId }),
    { credentials: "include", ...(signal ? { signal } : {}) },
  ).catch(() => null);
  if (response === null) return "failed";
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    return isRestrictedProvidersRefusal(response.status, body) ? "restricted" : "failed";
  }
  return Option.getOrElse(
    decodeBriefing(await response.json().catch(() => null)),
    (): ProbeBriefingFailure => "failed",
  );
};

export interface ProbeBriefingRead {
  readonly status: "loading" | "ready" | ProbeBriefingFailure;
  readonly briefing: ProbeBriefingType | null;
  /** Read the file again — after a failure, or to pick up a change. */
  readonly retry: () => void;
}

/** The briefing for this organization, with whether it is still coming or failed. */
export function useProbeBriefingRead(organizationId: string | null): ProbeBriefingRead {
  const [state, setState] = useState<{
    readonly organizationId: string | null;
    readonly key: string | null;
    readonly briefing: ProbeBriefingType | null;
    readonly failure: ProbeBriefingFailure | null;
  }>({ organizationId: null, key: null, briefing: null, failure: null });
  const [attempt, setAttempt] = useState(0);
  const key = organizationId === null ? null : `${organizationId}:${attempt}`;
  useEffect(() => {
    if (organizationId === null || key === null) return;
    const controller = new AbortController();
    void fetchProbeBriefing(organizationId, controller.signal).then((next) => {
      if (controller.signal.aborted) return;
      setState(
        typeof next === "string"
          ? { organizationId, key, briefing: null, failure: next }
          : { organizationId, key, briefing: next, failure: null },
      );
    });
    return () => controller.abort();
  }, [organizationId, key]);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  const current = state.key === key;
  // A retry keeps the last file on screen while it reads again — never
  // another organization's.
  const briefing =
    organizationId !== null && state.organizationId === organizationId ? state.briefing : null;
  return {
    status: !current ? "loading" : (state.failure ?? "ready"),
    briefing,
    retry,
  };
}

/** The briefing for this organization, or null until (or unless) it arrives. */
export function useProbeBriefing(organizationId: string | null): ProbeBriefingType | null {
  return useProbeBriefingRead(organizationId).briefing;
}
